import React, { ChangeEvent, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Select from "react-select";
import { ILinkFunc } from "../../lib/util";

ChartJS.register(ArcElement, Tooltip, Legend);

export enum EFooterLayout {
  centered = "centered",
  column = "column",
}

export interface MarketStatsProps {
  className?: string;
  /**
   * Header text/title to render, if any.
   */
  title?: string;
  /**
   * One or more MarketStatsMarket objects representing collections of market data to
   * display.  In the case of more than one instance, a selector/toggle is rendered to
   * switch displays.
   */
  markets: MarketStatsMarket[];
  statsContainerClassName?: string;
  statWrapperClassName?: string;
  statValueClassName?: string;
  statLabelClassName?: string;
  statDividerClassName?: string;
  footerLayout?: EFooterLayout;
  footerCopy?: string;
  linkAsButton?: boolean;
  linkFunc?: ILinkFunc;
  links?: { label: string; href: string }[];
  /**
   * When set to true the last displayed button href will
   * be replaced with the marketUrl from the selected market
   * AEFW-76
   */
  useMarketRealatedUrl?: boolean;
  /**
   * A text piece to be displayed (initially) between title and stats
   */
  text?: string;
  /**
   * Chart display flag
   */
  displayChart?: boolean;
  chartTitle?: string;
  chartData?: any;
  chartSettings?: any;
}

export interface MarketStatsMarket {
  marketLabel: string;
  marketData: MarketStatsStat[];
  marketUrl?: string;
}

export interface MarketStatsStat {
  label: string;
  value: any;
  annotation?: string;
}

export const MarketStats: React.FC<MarketStatsProps> = ({
  className,
  title,
  text,
  markets,
  statsContainerClassName = "",
  statWrapperClassName = "",
  statValueClassName = "",
  statLabelClassName = "",
  statDividerClassName = "",
  footerLayout = EFooterLayout.centered,
  footerCopy,
  linkAsButton = false,
  linkFunc,
  links,
  useMarketRealatedUrl = false,
  displayChart = false,
  chartTitle,
  chartData,
  chartSettings,
}) => {
  const selectStyles: any = {
    control: (base: object) => ({
      ...base,
      borderColor: "transparent",
      background: "transparent",
      borderBottomColor: "var(--color-accent)",
      borderRadius: 0,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      paddingRight: "0px",
      color: "var(--color-accent)",
    }),
    indicatorSeparator: (base: object) => ({
      ...base,
      display: "none",
    }),
    placeholder: (base: object) => ({
      ...base,
      color: "var(--color-button-text)",
      whiteSpace: "nowrap",
    }),
    valueContainer: (base: object) => ({
      ...base,
      paddingLeft: 0,
    }),
  };

  const footerLayoutVariant =
    footerLayout == EFooterLayout.centered
      ? "text-center flex-col"
      : "flex-wrap md:flex-nowrap";

  const linkStyleVariant = linkAsButton
    ? "py-2 px-3 leading-8 bg-button-bg text-button-text"
    : "text-xl underline";

  type ISelectOptionType = {
    value: number;
    label: string;
  };

  const marketOpts: ISelectOptionType[] = markets.map((item, index) => ({
    value: index,
    label: item.marketLabel,
  }));

  const [market, setMarket] = useState<ISelectOptionType>(marketOpts[0]);

  let lastLink = links && links.length > 0 ? links[(links.length - 1)].href : "";
  if (useMarketRealatedUrl && markets?.[0]?.marketUrl) {
    lastLink = markets[0].marketUrl
  }
  const [btnUrl, setBtnUrl] = useState(lastLink);

  const marketIndexOnChange = (option: any) => {
    if (useMarketRealatedUrl) {
      const selectedMarket = markets.find(item => item.marketLabel == option.label);
      selectedMarket && selectedMarket.marketUrl ? setBtnUrl(selectedMarket.marketUrl) : setBtnUrl(lastLink);
    }
    setMarket(option);
  };

  const marketDataElements = markets[market.value].marketData.map(
    (item, index) => (
      <div
        key={index}
        className={`w-full sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/6 text-center px-1 py-5 market-stat ${statWrapperClassName}`}
      >
        <div className={`font-h1 text-h1-text text-5xl tracking-widest market-stat-value ${statValueClassName}`}>
          {item.value}
        </div>
        <div className={`w-12 mx-auto bg-accent h-px my-3 market-stat-divider ${statDividerClassName}`}></div>
        <div
          className={`text-h2-text tracking-widest mx-auto market-stat-label ${statLabelClassName}`}
        >
          {item.label}
          {item.annotation && (
            <>
              <br />
              <span>{`(${item.annotation})`}</span>
            </>
          )}
        </div>
      </div>
    )
  );

  return (
    <div
      className={`flex flex-col items-center bg-transparent mx-auto relative z-10 max-w-screen-2xl ${className} market-stats`}
    >
      {title && <h2 className="pt-10">{title}</h2>}
      {(displayChart || text) && (
        <div className="w-full flex flex-row flex-wrap justify-center items-center">
          <div className={`w-full ${displayChart ? "md:w-2/3 md:pr-10 lg:pr-24" : ""}`} dangerouslySetInnerHTML={{ __html: text! }}></div>
          {displayChart && chartData && (
            <div className="w-full md:w-1/3 mt-10 md:mt-0 text-center market-stats-chart">
              {chartTitle && <span className="uppercase text-lg mb-7 inline-block">{chartTitle}</span>}
              <Pie plugins={[ChartDataLabels]} data={chartData} {...chartSettings} />
            </div>
          )}
        </div>
      )}
      {markets.length > 1 && (
        <label htmlFor="market">
          <span className="sr-only">Select market</span>
          <Select
            name="market"
            value={market}
            className="inline-block px-3 text-lg"
            onChange={(option) => marketIndexOnChange(option)}
            options={marketOpts}
            styles={selectStyles}
          />
        </label>
      )}
      <div className={`flex flex-wrap items-start justify-between my-12 w-full ${statsContainerClassName}`}>
        {marketDataElements}
      </div>
      {(footerCopy || links) && (
        <div
          className={`pb-12 px-4 flex gap-5 items-center justify-center ${footerLayoutVariant}`}
        >
          {footerCopy && <p className={`max-w-4xl`}>{footerCopy}</p>}
          {links &&
            links.map(
              (link, index) => {
                const linkHref = index == (links.length - 1) ? btnUrl : link.href
                return (
                  (linkFunc &&
                    linkFunc({
                      href: linkHref,
                      className: `uppercase font-bold whitespace-nowrap ${linkStyleVariant}`,
                      children: link.label,
                      key: index,
                    })) || (
                    <a
                      key={index}
                      href={linkHref}
                      className={`uppercase font-bold whitespace-nowrap ${linkStyleVariant}`}
                    >
                      {link.label}
                    </a>
                  )
                )
              }
            )}
        </div>
      )}
    </div>
  );
};

export default MarketStats;
